import Phaser from 'phaser';

import { BlackjackMainScene } from '../../scenes/blackjack-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { AvailableFontSizes, TextService } from '../../services/text-service';
import { PlayerMiniBetViewModel } from './player-mini-bet-view-model';
import { MiniChipView } from '../chips/mini-chip-view';
import { UxMetrics } from '../../model/ux-metrics';

export class PlayerMiniBetView extends Phaser.GameObjects.Container {
  public get chipGlobalPosition(): { x: number; y: number } {
    const point = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(this.miniChipView.x, this.miniChipView.y, point);
    return { x: point.x, y: point.y };
  }
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private miniChipView: MiniChipView;
  private betLabel: Phaser.GameObjects.Text;
  private currentBetAmount?: number;
  private activeTween?: Phaser.Tweens.Tween; // Store active tween

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: PlayerMiniBetViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.miniChipView = new MiniChipView(scene, 0, 0, viewModel.miniChipViewModel);
    this.add(this.miniChipView);

    const mediumFontStyle = TextService.fontStyleNormal(
      AvailableFontSizes.Medium,
      this.viewModel.labelFill,
      this.viewModel.defaultFontFamily,
    );

    this.betLabel = this.scene.add.text(0, 0, '', mediumFontStyle).setOrigin(0.5, 0.5);
    this.add(this.betLabel);

    this.setName(PlayerMiniBetView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.betAmount$.subscribe((betAmount) => {
        const prevValue = this.currentBetAmount ?? 0;

        this.setVisible(betAmount > 0);

        if (this.currentBetAmount === undefined) {
          this.currentBetAmount = betAmount;
          this.setBetAmount(this.currentBetAmount);
          return;
        }

        if (betAmount > 0 && prevValue !== 0) {
          this.animateBetAmount(
            this.currentBetAmount,
            betAmount,
            UxMetrics.NumberIncrementDuration,
          );
        } else {
          this.currentBetAmount = betAmount;
          this.setBetAmount(this.currentBetAmount);
        }
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.betDoubled$.subscribe((_) => {
        this.refreshLayout();
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.miniChipView?.onResize(resizeMetrics);

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private animateBetAmount(startNumber: number, endNumber: number, duration: number): void {
    // Stop any existing tween
    if (this.activeTween) {
      this.activeTween.stop();
    }

    const tempObj = { value: startNumber };

    this.activeTween = this.scene.tweens.add({
      targets: tempObj,
      value: endNumber,
      duration: duration,
      ease: Phaser.Math.Easing.Linear,
      onUpdate: () => {
        this.currentBetAmount = Math.round(tempObj.value); // Round for clarity
        this.setBetAmount(this.currentBetAmount);
      },
      onComplete: () => {
        this.activeTween = undefined; // Clear reference when done
      },
    });
  }

  private setBetAmount(value: number) {
    this.betLabel.text = value.toLocaleString();
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    this.betLabel.setFontSize(this.currentResizeMetrics.fontMetrics.Medium);

    const spacing = layoutConfig.paddingSm * scalingMetrics.scaleFactor;
    const controlsWidth = this.miniChipView.displayWidth + spacing + this.betLabel.displayWidth;
    const controlsHeight = this.miniChipView.displayHeight;

    this.setSize(controlsWidth, controlsHeight);

    this.miniChipView.setX(-spacing * 0.5 - this.betLabel.displayWidth * 0.5);
    this.betLabel.setX(spacing * 0.5 + this.miniChipView.displayWidth * 0.5);
  }
}
